import React, { useState } from 'react';
import { Stethoscope, Pill, Activity, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/Card';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { SectionHeader } from './ui/SectionHeader';
import { ReflexologyChartsModal } from './ReflexologyChartsModal';
import { MedicalFormData } from '../hooks/useFormState';
interface DiagnosisTreatmentProps {
  data: MedicalFormData;
  onChange: (field: keyof MedicalFormData, value: any) => void;
}
export function DiagnosisTreatment({
  data,
  onChange
}: DiagnosisTreatmentProps) {
  const [isChartsModalOpen, setIsChartsModalOpen] = useState(false);
  const handleTreatmentModalityToggle = (modality: string) => {
    const current = data.treatmentModalities || [];
    const updated = current.includes(modality) ? current.filter(m => m !== modality) : [...current, modality];
    onChange('treatmentModalities', updated);
  };
  return <Card className="h-full" data-id="element-36">
      <CardHeader data-id="element-37">
        <SectionHeader title="Diagnosis, Treatment & Advice" subtitle="Provisional diagnosis and treatment plan" icon={<Stethoscope className="w-5 h-5" data-id="element-39" />} data-id="element-38" />
      </CardHeader>
      <CardContent className="space-y-6" data-id="element-40">
        <ReflexologyChartsModal isOpen={isChartsModalOpen} onClose={() => setIsChartsModalOpen(false)} data-id="element-41" />

        {/* Diagnosis Section */}
        <div data-id="element-42">
          <Label className="text-base font-semibold text-amber-900 mb-3 block" data-id="element-43">
            Diagnosis (Provisional)
          </Label>
          <Textarea value={data.diagnosis} onChange={e => onChange('diagnosis', e.target.value)} placeholder="Provisional diagnosis notes..." className="min-h-[100px] bg-yellow-50/50 border-yellow-200 focus:border-yellow-400 focus:ring-yellow-200" data-id="element-44" />
        </div>

        {/* Treatment Plan */}
        <div className="pt-6 border-t-2 border-amber-200" data-id="element-45">
          <Label className="text-base font-semibold text-amber-900 mb-3 block" data-id="element-46">
            Treatment Plan
          </Label>
          <div className="space-y-3" data-id="element-47">
            <div data-id="element-48">
              <Label data-id="element-49">Even Date (Right / Front)</Label>
              <Textarea value={data.evenDate} onChange={e => onChange('evenDate', e.target.value)} placeholder="Treatment plan for even dates (right side / front)..." rows={3} data-id="element-50" />
            </div>
            <div data-id="element-51">
              <Label data-id="element-52">Odd Date (Left / Back)</Label>
              <Textarea value={data.oddDate} onChange={e => onChange('oddDate', e.target.value)} placeholder="Treatment plan for odd dates (left side / back)..." rows={3} data-id="element-53" />
            </div>
          </div>
        </div>

        {/* Reflexology Points */}
        <div className="pt-6 border-t-2 border-amber-200" data-id="element-54">
          <div className="flex items-center justify-between mb-4" data-id="element-55">
            <div className="flex items-center gap-2" data-id="element-56">
              <Activity className="w-5 h-5 text-amber-600" data-id="element-57" />
              <h3 className="text-base font-bold text-amber-900" data-id="element-58">
                Reflexology Points
              </h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => setIsChartsModalOpen(true)} className="border-amber-600 text-amber-700 hover:bg-amber-50 print:hidden" data-id="element-59">
              <Eye className="w-4 h-4 mr-2" data-id="element-60" />
              View Charts
            </Button>
          </div>

          <div className="bg-amber-50/50 p-5 rounded-lg border-2 border-amber-200" data-id="element-61">
            <div className="mb-4" data-id="element-62">
              <p className="text-sm text-stone-700 font-medium mb-3" data-id="element-63">
                Standard Points:
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-64">
                {[3, 4, 8, 15, 16, 25, 28, 38, 29, 9, 26].map(point => <span key={point} className="inline-flex items-center justify-center bg-amber-600 text-white font-semibold px-3 py-1.5 rounded-lg text-sm" data-id="element-65">
                    {point}
                  </span>)}
              </div>
            </div>

            <div data-id="element-66">
              <Label data-id="element-67">Additional Points / Notes</Label>
              <Input value={data.reflexologyPointsText} onChange={e => onChange('reflexologyPointsText', e.target.value)} placeholder="Enter additional reflexology points or notes..." data-id="element-68" />
            </div>
          </div>
        </div>

        {/* Treatment Modalities */}
        <div className="pt-6 border-t-2 border-amber-200" data-id="element-69">
          <div className="flex items-center gap-2 mb-4" data-id="element-70">
            <Pill className="w-5 h-5 text-amber-600" data-id="element-71" />
            <h3 className="text-base font-bold text-amber-900" data-id="element-72">
              Treatment Modalities
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 bg-amber-50/50 p-5 rounded-lg border-2 border-amber-200" data-id="element-73">
            {['Acupuncture Needles', 'Pachi', 'Sujok (Needles)', 'Sujok(Magnets)', 'Sujok(Colour)', 'Colours on Classic', 'Magnets on Classic', 'Magnets on Byol', 'Seeds', 'Acupressure Only', 'Blood Letting', 'TENS', 'Cupping', 'Moxa', 'Body Massager', 'Electro Acupuncture Device', 'Electro Acupuncture Pen', 'Auricular', '7 Needle Hammer(plum blossom hammer)', 'Derma Roller', 'Infrared', 'Gua Sha'].map(modality => <label key={modality} className="flex items-center space-x-3 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-74">
                <input type="checkbox" className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.treatmentModalities || []).includes(modality)} onChange={() => handleTreatmentModalityToggle(modality)} data-id="element-75" />
                <span className="text-sm text-stone-700" data-id="element-76">{modality}</span>
              </label>)}
          </div>
        </div>
      </CardContent>
    </Card>;
}